import { ControlApiCall } from './api/Control';
import { CommandSerializer } from './api/Control/CommandSerializer/CommandSerializer';
import { Vehicle } from 'core/models/vehicle';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';
import { DriveCommand, Gear, ControlMode, EmergencyMode, DriveMode, isDriveModePriorThan } from 'core/models/drive';
import { DriveCommandSerializer } from './api/Control/CommandSerializer/DriveCommandSerializer';
import { StableWebSocket } from 'infrastructure/utility/StableWebSocket';

const REDUCE_DRIVE_COMMAND = false;

const VEHICLE_COMMAND_UPDATE_INTERVAL = 50; // [ms]

function accumulateCommands(currentStatus: DriveCommand, delta: Partial<DriveCommand>): DriveCommand {
    const accumulated = {
        ...currentStatus,
        ...delta,
    };

    return accumulated;
}

function equalCommands(lhs: DriveCommand, rhs: DriveCommand): boolean {
    if (!REDUCE_DRIVE_COMMAND) {
        return false;
    }

    if (lhs.steering !== rhs.steering) return false;
    if (lhs.accel !== rhs.accel) return false;
    if (lhs.brake !== rhs.brake) return false;
    if (lhs.blinker !== rhs.blinker) return false;
    if (lhs.gear !== rhs.gear) return false;
    if (lhs.controlMode !== rhs.controlMode) return false;
    if (lhs.emergency !== rhs.emergency) return false;
    return true;
}

type CommandStatus = {
    command: DriveCommand;
    isSent: boolean;
    isControlModeChanged: boolean;
};

type Entry = {
    projectId: Project.Id;
    commandStatus: CommandStatus;
}

export class VehicleStatusPublisher {
    private readonly controlApiCall: ControlApiCall;
    private readonly commandSerializer: CommandSerializer;
    private readonly controlStatus = new Map<Vehicle.Id, Entry>();
    private controlInterval?: number;
    private ws: StableWebSocket;
    private readonly onError: (error: unknown) => void;

    constructor(controlApiCall: ControlApiCall, ws: StableWebSocket, onError: (error: unknown) => void) {
        this.onError = onError;
        this.controlApiCall = controlApiCall;
        this.commandSerializer = new DriveCommandSerializer({ withTimestamp: true });
        this.ws = ws;
    }

    async postDriveCommand(vehicleId: Vehicle.Id, projectId: Project.Id, environmentId: Environment.Id, commandDelta: Partial<DriveCommand>): Promise<void> {
        if (this.controlStatus.size == 0) {
            this.startPublishIfNeeded(); // intentionally non-awaited
        }

        const initialStatus = {
            command: {
                steering: 0,
                accel: 0,
                brake: 0,
                gear: Gear.P,
                blinker: null,
                controlMode: ControlMode.Auto,
                emergency: EmergencyMode.Normal,
            },
            isUnsent: true,
            isControlModeChanged: true,
        };
        const current = this.controlStatus.get(vehicleId)?.commandStatus ?? initialStatus;
        const nextCommand = accumulateCommands(current.command, commandDelta);
        if (current.command.emergency !== nextCommand.emergency) {
            this.putEmergencyMode(vehicleId, projectId, environmentId, nextCommand.emergency); // intentionally non-awaited
        }
        if (current.command.controlMode !== nextCommand.controlMode) {
            console.log(`start changing mode from ${current.command.controlMode} to ${nextCommand.controlMode}`);
        }
        const isControlModeChanged = current.command.controlMode !== nextCommand.controlMode
            || current.isControlModeChanged;
        const updated = {
            command: nextCommand,
            isSent: current.command && equalCommands(current.command, nextCommand),
            isControlModeChanged,
        };
        this.controlStatus.set(vehicleId, { projectId, commandStatus: updated });
    }

    async cancelPostDriveCommand(vehicleId: Vehicle.Id): Promise<void> {
        this.controlStatus.delete(vehicleId);

        if (this.controlStatus.size == 0) {
            this.stopPublish();
        }
    }

    private readonly latestVehicleStatusMap = new Map<Vehicle.Id, Vehicle.Status | null>();

    setLatestStatus(vehicleId: Vehicle.Id, status: Vehicle.Status) {
        this.latestVehicleStatusMap.set(vehicleId, status);
    }

    private async putEmergencyMode(vehicleId: Vehicle.Id, projectId: Project.Id, environmentId: Environment.Id, emergencyMode: EmergencyMode) {
        function emergencyModeToOnOff(emergencyMode: EmergencyMode) {
            switch (emergencyMode) {
                case EmergencyMode.Normal:
                    return 0;
                case EmergencyMode.Emergency:
                case EmergencyMode.EmergencyAll:
                    return 1;
            }
        }
        const mode = emergencyModeToOnOff(emergencyMode);
        const res = await this.controlApiCall(`/v1/projects/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/status/emergency`, {
            method: 'PUT',
            body: JSON.stringify({ mode })
        });

        return res;
    }

    private async startPublishIfNeeded() {
        this.controlInterval = window.setInterval(() => {
            (async () => {
                try {
                    this.controlStatus.forEach(({ projectId, commandStatus: tmp }, vehicleId) => {
                        const isControlModeChanged = tmp.isControlModeChanged;
                        const isRemoteMode = tmp.command.controlMode === ControlMode.Remote;
                        const isEmergency = tmp.command.emergency !== EmergencyMode.Normal;
                        const peerStatus = this.latestVehicleStatusMap.get(vehicleId) ?? undefined;
                        const isUncontrollableFromRemote =
                            peerStatus?.mode === undefined
                            || isDriveModePriorThan(peerStatus.mode, DriveMode.Remote);
                        if (isUncontrollableFromRemote) return;
                        const peerControlMode = peerStatus
                            ? peerStatus.mode === DriveMode.Remote ? ControlMode.Remote : ControlMode.Auto
                            : undefined;
                        if (!isControlModeChanged && !isRemoteMode && !isEmergency) return;
                        if (tmp.isSent) return;

                        if (peerControlMode === tmp.command.controlMode) {
                            if (tmp.isControlModeChanged) {
                                console.log(`finish changing mode to ${peerControlMode}`);
                            }
                            tmp.isControlModeChanged = false;
                        }
                        if (REDUCE_DRIVE_COMMAND) {
                            tmp.isSent = true;
                        }

                        const payload = this.commandSerializer.serialize(vehicleId, projectId, tmp.command);
                        this.ws.send(payload);
                    });
                } catch (e) {
                    console.error(`cannot post command due to: ${JSON.stringify(e)}`);
                    this.onError(e);
                }
            })();
        }, VEHICLE_COMMAND_UPDATE_INTERVAL);
    }

    private stopPublish() {
        if (this.controlStatus.size > 0 || this.controlInterval == undefined) {
            return;
        }

        clearInterval(this.controlInterval);
        this.controlInterval = undefined;
    }
}